export abstract class Register
{
    public static make8(buffer: Uint8Array = new Uint8Array(1), offset: number = 0): Register8
    {
        return new Register8(buffer, offset);
    }

    public static make16(buffer: Uint8Array = new Uint8Array(2), offset: number = 0): Register16
    {
        return new Register16(buffer, offset);
    }

    /**
     * Returns the raw value from this register as a number.
     */
    public abstract get value(): number;

    /**
     * Sets the raw value of this register.
     *
     * @remarks The given value is masked against the register type to ensure
     *          the value stays within its boundary.
     */
    public abstract set value(val: number);

    protected readonly buffer: Uint8Array;
    protected readonly offset: number;
    protected abstract readonly size: number;

    protected constructor(buffer: Uint8Array, offset: number = 0)
    {
        this.buffer = buffer;
        this.offset = offset;
    }

    /**
     * Sets a bit at the given location.
     *
     * @param bit The position of the bit to set (0-based).
     * @param state The state to set the bit to (true for 1, false for 0).
     */
    public setBit(bit: number, state: boolean): void
    {
        let val = this.value;

        if (state) {
            val |= (1 << bit);
        } else {
            val &= ~(1 << bit);
        }

        this.value = val;
    }

    /**
     * Returns true if a bit at the given location is set.
     *
     * @param bit The position of the bit to get (0-based)
     * @returns boolean
     */
    public hasBit(bit: number): boolean
    {
        return ((this.value >> bit) & 1) === 1;
    }

    /**
     * Returns a bit at the given location.
     *
     * @param bit The position of the bit to get (0-based)
     * @returns boolean
     */
    public getBit(bit: number): number
    {
        return (this.value >> bit) & 1;
    }

    /**
     * Returns a set of bits at the given offset of the given size within the
     * data of this register.
     *
     * @param offset The starting offset (bit)
     * @param size The amount of bits to extract.
     */
    public getBits(offset: number, size: number): number
    {
        return (this.value >> offset) & ((1 << size) - 1);
    }

    /**
     * Sets a sequence of bits at the given offset of the given size within the
     * data of this register.
     *
     * @param offset The starting offset (bit).
     * @param size The amount of bits to replace.
     * @param val The value to set within the given bit range.
     */
    public setBits(offset: number, size: number, val: number): void
    {
        const mask: number  = (1 << size) - 1;
        let current: number = this.value;

        current &= ~(mask << offset);
        current |= (val & mask) << offset;

        this.value = current;
    }

    /**
     * Returns a string representation of this register in hexadecimal format
     * in groups of 2 digits (1 byte) separated by spaces. For example, a 16-bit
     * register with value 0x1234 would be represented as "34 12" (LE). Negative
     * values will be prefixed with a '-'.
     */
    public toString(): string
    {
        const value: number  = this.value;
        const absVal: number = Math.abs(value);
        const prefix: string = value < 0 ? '-' : '';

        const hexParts: string[] = [];

        for (let i = 0; i < this.size; i++) {
            const byte = (absVal >> (8 * i)) & 0xFF;
            hexParts.push(byte.toString(16).padStart(2, '0'));
        }

        return prefix + hexParts.join(' ');
    }
}

export class Register8 extends Register
{
    protected readonly size = 1;

    /**
     * @inheritDoc
     */
    public get value(): number
    {
        return this.buffer[this.offset];
    }

    /**
     * @inheritDoc
     */
    public set value(val: number)
    {
        this.buffer[this.offset] = val & 0xFF;
    }
}

export class Register16 extends Register
{
    protected readonly size = 2;

    /**
     * @inheritDoc
     */
    public get value(): number
    {
        return (this.buffer[this.offset + 1] << 8) | this.buffer[this.offset];
    }

    /**
     * @inheritDoc
     */
    public set value(val: number)
    {
        const masked: number = val & 0xFFFF;

        this.buffer[this.offset]     = masked & 0xFF;
        this.buffer[this.offset + 1] = (masked >> 8) & 0xFF;
    }

    /**
     * Returns the low byte of this 16 bit register.
     */
    public get low(): number
    {
        return this.buffer[this.offset];
    }

    /**
     * Returns the high byte of this 16 bit register.
     */
    public get high(): number
    {
        return this.buffer[this.offset + 1];
    }

    /**
     * Sets the low value of this 16 bit register.
     */
    public set low(val: number)
    {
        this.buffer[this.offset] = val & 0xFF;
    }

    /**
     * Sets the high value of this 16 bit register.
     */
    public set high(val: number)
    {
        this.buffer[this.offset + 1] = val & 0xFF;
    }
}
